/**
 * Results Visualization
 * 
 * Visualizes structural analysis results: stress diagrams, deflection contours.
 */

import * as THREE from 'three';
import { AnalysisResults, MemberForces } from './FEASolver';

// ==================== Types ====================

export type DiagramType =
    | 'AxialForce'
    | 'ShearY'
    | 'ShearZ'
    | 'MomentY'
    | 'MomentZ'
    | 'Torsion'
    | 'Stress'
    | 'Deflection';

export type ColorScale = 'Rainbow' | 'BlueRed' | 'Grayscale' | 'Custom';

export interface DiagramSettings {
    type: DiagramType;
    scale: number;
    colorScale: ColorScale;
    showValues: boolean;
    showLegend: boolean;
    minValue?: number;
    maxValue?: number;
}

export interface DiagramData {
    memberId: string;
    stations: number[];
    values: number[];
    colors: THREE.Color[];
    positions: THREE.Vector3[];
}

export interface ContourData {
    positions: Float32Array;
    colors: Float32Array;
    values: number[];
}

// ==================== Color Scales ====================

function rainbowColor(t: number): THREE.Color {
    // Rainbow: Blue -> Cyan -> Green -> Yellow -> Red
    const hue = (1 - t) * 0.7;  // 0.7 = blue, 0 = red
    return new THREE.Color().setHSL(hue, 1, 0.5);
}

function blueRedColor(t: number): THREE.Color {
    // Blue (0) -> White (0.5) -> Red (1)
    if (t < 0.5) {
        const tt = t * 2;
        return new THREE.Color(tt, tt, 1);
    } else {
        const tt = (t - 0.5) * 2;
        return new THREE.Color(1, 1 - tt, 1 - tt);
    }
}

function grayscaleColor(t: number): THREE.Color {
    return new THREE.Color(t, t, t);
}

// ==================== Results Visualization ====================

export class ResultsVisualization {
    private settings: DiagramSettings = {
        type: 'MomentY',
        scale: 1,
        colorScale: 'Rainbow',
        showValues: true,
        showLegend: true
    };

    // ==================== Force Diagrams ====================

    public generateForceDiagram(
        results: AnalysisResults,
        memberPositions: Map<string, { start: THREE.Vector3; end: THREE.Vector3 }>
    ): DiagramData[] {
        const diagrams: DiagramData[] = [];

        for (const [memberId, forces] of results.memberForces) {
            const positions = memberPositions.get(memberId);
            if (!positions) continue;

            const diagram = this.createMemberDiagram(memberId, forces, positions);
            diagrams.push(diagram);
        }

        return diagrams;
    }

    private createMemberDiagram(
        memberId: string,
        forces: MemberForces,
        positions: { start: THREE.Vector3; end: THREE.Vector3 }
    ): DiagramData {
        const values = this.getForceValues(forces);
        const { minVal, maxVal } = this.getMinMax(values);

        const direction = positions.end.clone().sub(positions.start);
        const length = direction.length();
        direction.normalize();

        // Perpendicular direction for diagram offset
        const up = new THREE.Vector3(0, 0, 1);
        const perp = direction.clone().cross(up).normalize();

        const diagramPositions: THREE.Vector3[] = [];
        const colors: THREE.Color[] = [];

        for (let i = 0; i < forces.stations.length; i++) {
            const t = forces.stations[i];
            const value = values[i];

            // Position along member
            const basePos = positions.start.clone().add(direction.clone().multiplyScalar(t * length));

            // Offset by scaled value
            const offset = perp.clone().multiplyScalar(value * this.settings.scale / 1000);
            const pos = basePos.add(offset);

            diagramPositions.push(pos);
            colors.push(this.getColor(value, minVal, maxVal));
        }

        return {
            memberId,
            stations: forces.stations,
            values,
            colors,
            positions: diagramPositions
        };
    }

    private getForceValues(forces: MemberForces): number[] {
        switch (this.settings.type) {
            case 'AxialForce': return forces.axialForce;
            case 'ShearY': return forces.shearY;
            case 'ShearZ': return forces.shearZ;
            case 'MomentY': return forces.momentY;
            case 'MomentZ': return forces.momentZ;
            case 'Torsion': return forces.torsion;
            case 'Stress': return forces.stress;
            default: return forces.momentY;
        }
    }

    // ==================== Deflection Contours ====================

    public generateDeflectionContour(
        results: AnalysisResults,
        nodePositions: Map<string, THREE.Vector3>
    ): ContourData {
        const positions: number[] = [];
        const colors: number[] = [];
        const values: number[] = [];

        // Get min/max displacements
        let maxDisp = 0;
        for (const disp of results.displacements.values()) {
            const mag = Math.sqrt(disp.dx ** 2 + disp.dy ** 2 + disp.dz ** 2);
            maxDisp = Math.max(maxDisp, mag);
        }

        for (const [nodeId, disp] of results.displacements) {
            const origPos = nodePositions.get(nodeId);
            if (!origPos) continue;

            // Deformed position
            const defPos = origPos.clone().add(
                new THREE.Vector3(
                    disp.dx * this.settings.scale,
                    disp.dy * this.settings.scale,
                    disp.dz * this.settings.scale
                )
            );

            positions.push(defPos.x, defPos.y, defPos.z);

            const mag = Math.sqrt(disp.dx ** 2 + disp.dy ** 2 + disp.dz ** 2);
            values.push(mag);

            const t = maxDisp > 0 ? mag / maxDisp : 0;
            const color = this.getColor(t, 0, 1);
            colors.push(color.r, color.g, color.b);
        }

        return {
            positions: new Float32Array(positions),
            colors: new Float32Array(colors),
            values
        };
    }

    // ==================== Color Mapping ====================

    private getColor(value: number, minVal: number, maxVal: number): THREE.Color {
        const range = maxVal - minVal;
        const t = range > 0 ? (value - minVal) / range : 0.5;

        switch (this.settings.colorScale) {
            case 'Rainbow': return rainbowColor(t);
            case 'BlueRed': return blueRedColor(t);
            case 'Grayscale': return grayscaleColor(t);
            default: return rainbowColor(t);
        }
    }

    private getMinMax(values: number[]): { minVal: number; maxVal: number } {
        if (this.settings.minValue !== undefined && this.settings.maxValue !== undefined) {
            return { minVal: this.settings.minValue, maxVal: this.settings.maxValue };
        }

        let minVal = Infinity;
        let maxVal = -Infinity;
        for (const v of values) {
            minVal = Math.min(minVal, v);
            maxVal = Math.max(maxVal, v);
        }
        return { minVal, maxVal };
    }

    // ==================== Legend ====================

    public generateLegend(minVal: number, maxVal: number, steps: number = 10): { value: number; color: THREE.Color }[] {
        const legend: { value: number; color: THREE.Color }[] = [];

        for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const value = minVal + t * (maxVal - minVal);
            legend.push({
                value,
                color: this.getColor(value, minVal, maxVal)
            });
        }

        return legend;
    }

    // ==================== Settings ====================

    public setSettings(settings: Partial<DiagramSettings>): void {
        Object.assign(this.settings, settings);
    }

    public getSettings(): DiagramSettings {
        return { ...this.settings };
    }

    public setDiagramType(type: DiagramType): void {
        this.settings.type = type;
    }

    public setScale(scale: number): void {
        this.settings.scale = scale;
    }

    public setColorScale(colorScale: ColorScale): void {
        this.settings.colorScale = colorScale;
    }
}
